import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { PaylinkService } from 'src/app/services/paylink.service';
import { UiFeedbackService } from 'src/app/ui/ui-feedback.service';
import { v4 as uuidv4 } from 'uuid';

declare const sha512: any;

@Component({ standalone: false,
  selector: 'app-form-paylink',
  templateUrl: './form-paylink.component.html',
  styleUrls: ['./form-paylink.component.css']
})
export class FormPaylinkComponent implements OnInit, OnDestroy {

  myForm: FormGroup;
  minicartItems: FormGroup;
  orderIdInterval: any;
  orderId: string = '';
  merchantCode: string = '';
  orderIdentifier: string = '';
  postMessageListener: ((event: MessageEvent) => void) | null = null;
  hasReceivedSdkMessage: boolean = false;
  items: any = [
    {
      "name": "Boleto",
      "quantity": 1,
      "unitAmount": 1,
      "amount": 1,
      "sku": "1020304005060",
      "category": "Entrada",
      "discount": 0,
      "shipping": 0
    }
  ];

  paylinkUrl: string = '';
  isLoading: boolean = false;
  errorMessage: string = '';
  showIframe: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private paylinkService: PaylinkService,
    private uiFeedback: UiFeedbackService,
    private cdr: ChangeDetectorRef,
  ) {
    const autoOrderId = this.generateOrderId();

    this.myForm = this.formBuilder.group({
      'environment': ['QA'],
      'merchantKey': ['OCC-40467ba5-f254-4cf0-937e-9f42a206cb3f', Validators.required],
      'secretKey': ['93ffbb5b3e574d76a89b43ded9daa0e4', Validators.required],
      'orderId': [autoOrderId, Validators.required],
      'currency': ['PEN'],
      'amount': [1],
      'discounts': [0],
      'shipping': [0],
      'taxes': [0],
      'fee': [0],
      'email': ['test@test.com', Validators.required],
      'name': ['Rafael', Validators.required],
      'lastname': ['Romero', Validators.required],
      'phone': ['999555666', Validators.required],
      'identifier': ['a123456a-d123-4e3a-bcf4-aea1234f5f93'],
      'documentType': ['DNI'],
      'documentNumber': ['44556677', Validators.required],
      'address1': ['Ca Nueve 1802'],
      'addressCountry': ['PER'],
      'addressLevel1': ['LIMA'],
      'addressLevel2': ['LIMA'],
      'addressLevel3': ['SAN ISIDRO'],
      'addressZip': ['15036'],
      'expirationDate': ['2029-12-31T23:59:59.999-0500'],
      'qrPayment': [false],
      'cardRegistered': [false],
      'integrationMode': [null],
      'createdFromVtex': [false],
      'paymentMethod': ['Tarjetas-Credito-Debito-PROM'],
    });

    this.minicartItems = this.formBuilder.group({
      "name": ['Boleto'],
      "quantity": [1],
      "unitAmount": [1],
      "amount": [1],
      "sku": ['1020304005060'],
      "category": ['Entrada'],
      "discount": [0],
      "shipping": [0]
    });

    this.myForm.get('amount')?.setValue(this.calculateTotal());
  }

  ngOnInit(): void {
    this.myForm.valueChanges.subscribe(() => {
      this.myForm.get('amount')?.setValue(this.calculateTotal(), { emitEvent: false });
    });

    this.myForm.get('qrPayment')?.valueChanges.subscribe((checked) => {
      if (checked) {
        this.myForm.get('cardRegistered')?.setValue(false, { emitEvent: false });
      }
    });

    this.myForm.get('cardRegistered')?.valueChanges.subscribe((checked) => {
      if (checked) {
        this.myForm.get('qrPayment')?.setValue(false, { emitEvent: false });
      }
    });

    // Registrar listener de postMessage
    this.postMessageListener = (event: MessageEvent) => {
      this.handlePostMessage(event);
    };
    window.addEventListener('message', this.postMessageListener);
    console.log('🎧 PostMessage listener registrado y escuchando...');

    // Actualizar el orderId cada segundo (solo si no hay iframe mostrado)
    this.startOrderIdInterval();
  }

  ngOnDestroy(): void {
    // Limpiar el intervalo al destruir el componente
    if (this.orderIdInterval) {
      clearInterval(this.orderIdInterval);
    }

    // Limpiar el listener de postMessage
    if (this.postMessageListener) {
      window.removeEventListener('message', this.postMessageListener);
    }
  }

  generateOrderId(): string {
    // Generar orderId basado en segundos (en lugar de milisegundos)
    const secondsTimestamp = Math.floor(Date.now() / 1000);
    return 'ORD' + secondsTimestamp;
  }

  startOrderIdInterval(): void {
    // Solo iniciar si no existe ya un intervalo
    if (!this.orderIdInterval) {
      this.orderIdInterval = setInterval(() => {
        const newOrderId = this.generateOrderId();
        this.myForm.get('orderId')?.setValue(newOrderId, { emitEvent: false });
      }, 1000);
      console.log('⏰ Intervalo de OrderId iniciado');
    }
  }

  stopOrderIdInterval(): void {
    if (this.orderIdInterval) {
      clearInterval(this.orderIdInterval);
      this.orderIdInterval = null;
      console.log('⏰ Intervalo de OrderId detenido');
    }
  }

  handlePostMessage(event: MessageEvent): void {
    console.log('📨 windows.postMessage recibido:', {
      data: event.data,
      origin: event.origin,
      source: event.source
    });
    if (event.data?.source === 'PAYLINK_SDK' || event.origin?.includes('pay.link.epago.pe')) {
      this.hasReceivedSdkMessage = true;
    }
    // Intentar parsear si es string
    if (typeof event.data === 'string') {
      try {
        const parsed = JSON.parse(event.data);
        console.log('📦 Data (parsed):', parsed);
      } catch (e) {
        console.log('⚠️ No se pudo parsear como JSON');
      }
    }

    // Verificar si es el mensaje de PayLink
    if (event.data?.type === 'epago.payment-completed' || event.data?.type === 'EPAGO_PAYMENT_COMPLETED') {
      setTimeout(() => {
        this.closeIframe();
      }, 3000);
    }
  }

  calculateTotal(): number {
    let total: number = 0;

    if (this.items.length > 0) {
      total = this.items.reduce((amount: any, current: { amount: any; discount: any; shipping: any }) => {
        const discountNegative = -(Math.sign(current.discount) === 1 ? current.discount : -current.discount);
        return amount + (current.amount + discountNegative + current.shipping);
      }, 0);
    }

    const discountValue = this.myForm.get('discounts')?.value;
    const discount = -(Math.sign(discountValue) === 1 ? discountValue : -discountValue);
    const shipping = Number(this.myForm.get('shipping')?.value);
    const taxes = Number(this.myForm.get('taxes')?.value);
    const fee = Number(this.myForm.get('fee')?.value);

    return total + discount + shipping + taxes + fee;
  }

  generateSignature(identifier: string, orderNumber: string, currencyCode: string, amount: number, signatureKey: string): string {
    const signatureBase = identifier + orderNumber + currencyCode + amount + signatureKey;
    const signature = sha512(signatureBase);
    return signature;
  }

  newMinicartItem(): any {
    const discountValue = this.minicartItems.get('discount')?.value;

    return {
      name: this.minicartItems.get('name')?.value,
      quantity: this.minicartItems.get('quantity')?.value,
      unitAmount: +this.minicartItems.get('amount')?.value,
      amount: (+this.minicartItems.get('amount')?.value) * this.minicartItems.get('quantity')?.value,
      sku: this.minicartItems.get('sku')?.value,
      category: this.minicartItems.get('category')?.value,
      discount: -(Math.sign(discountValue) === 1 ? discountValue : -discountValue),
      shipping: Number(this.minicartItems.get('shipping')?.value)
    };
  }

  addMinicartItem(): void {
    this.items.push(this.newMinicartItem());
    this.myForm.get('amount')?.setValue(this.calculateTotal());
  }

  removeMinicartItem(i: number): void {
    this.items.splice(i, 1);
    this.myForm.get('amount')?.setValue(this.calculateTotal());
  }

  private buildVtexId(): string {
    return uuidv4().replace(/-/g, '').toUpperCase();
  }

  private buildOrderMetadata(): Array<{ name: string; value: string }> {
    if (!this.myForm.get('createdFromVtex')?.value) {
      return [];
    }

    const transactionId = this.buildVtexId();
    const paymentId = this.buildVtexId();
    const reference = String(Math.floor(Date.now() / 1000) % 1000000).padStart(6, '0');
    const returnUrl =
      `https://plazaveaambienteqa.vtexpayments.com.br/payment-provider/transactions/${transactionId}/payments/${paymentId}/return?accountName=plazaveaambienteqa`;

    return [
      { name: 'createdFromVtex', value: 'true' },
      { name: 'returnUrl', value: returnUrl },
      { name: 'VTEX.reference', value: reference },
      { name: 'VTEX.transactionId', value: transactionId },
      { name: 'VTEX.paymentId', value: paymentId },
      { name: 'VTEX.installments.number', value: '0' },
      { name: 'VTEX.installments.deferred', value: '0' },
      { name: 'createdFromLink', value: 'true' },
    ];
  }

  createPaylink(): void {
    if (this.myForm.invalid) {
      this.errorMessage = 'Por favor, completa todos los campos requeridos.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.showIframe = false;
    this.paylinkUrl = '';

    const identifier = this.myForm.get('merchantKey')?.value;
    const signatureKey = this.myForm.get('secretKey')?.value;
    const orderNumber = this.myForm.get('orderId')?.value;
    const currencyCode = this.myForm.get('currency')?.value;
    const amount = this.calculateTotal();

    const signature = this.generateSignature(identifier, orderNumber, currencyCode, amount, signatureKey);

    // Generar signedElement: orderNumber + currencyCode + amount
    const signedElement = orderNumber + currencyCode + amount;

    const discountValue = this.myForm.get('discounts')?.value;
    const cardRegistered = this.myForm.get('cardRegistered')?.value === true;

    const orderData = {
      "order": {
        "number": orderNumber,
        "type": "ORDER",
        "qrPayment": this.myForm.get('qrPayment')?.value,
        ...(cardRegistered && { cardRegister: true }),
        "integrationMode": this.myForm.get('integrationMode')?.value,
        "country": {
          "code": "PER"
        },
        "currency": {
          "code": currencyCode
        },
        "amountEntryIndicator": false,
        "amount": amount,
        "discounts": -(Math.sign(discountValue) === 1 ? discountValue : -discountValue),
        "taxes": Number(this.myForm.get('taxes')?.value),
        "fee": Number(this.myForm.get('fee')?.value),
        "shippingAmount": Number(this.myForm.get('shipping')?.value),
        "dispatch": "HOME_DELIVERY",
        "products": [...this.items],
        "multipleCustomerIndicator": false,
        "customer": {
          "identifier": this.myForm.get('identifier')?.value,
          "name": this.myForm.get('name')?.value,
          "lastName": this.myForm.get('lastname')?.value,
          "phone": this.myForm.get('phone')?.value,
          "email": this.myForm.get('email')?.value,
          "document": {
            "type": this.myForm.get('documentType')?.value,
            "number": this.myForm.get('documentNumber')?.value
          },
          "address": {
            "country": this.myForm.get('addressCountry')?.value,
            "levels": [
              this.myForm.get('addressLevel1')?.value,
              this.myForm.get('addressLevel2')?.value,
              this.myForm.get('addressLevel3')?.value
            ],
            "line1": this.myForm.get('address1')?.value,
            "zip": this.myForm.get('addressZip')?.value
          }
        },
        "metadata": this.buildOrderMetadata()
      },
      "settings": {
        "expiration": {
          "type": "DATE",
          "date": this.myForm.get('expirationDate')?.value
        },
        "paymentMethod": this.myForm.get('paymentMethod')?.value,
        "language": "es_PE"
      }
    };

    const environment = this.myForm.get('environment')?.value || 'QA';

    console.log('Order Data:', orderData);
    console.log('Identifier:', identifier);
    console.log('Signature:', signature);
    console.log('Signed Element (generado):', signedElement);
    console.log('Ambiente create:', environment, PaylinkService.API_URLS[environment] || PaylinkService.API_URLS['QA']);

    this.paylinkService.createOrder(orderData, identifier, signature, signedElement, environment)
      .subscribe({
        next: (response) => {
          console.log('Response completa:', response);
          this.isLoading = false;

          this.orderIdentifier = response?.data?.order?.identifier;
          this.orderId = response?.data?.order?.number;
          this.merchantCode = response?.data?.order?.entity?.identifier;

          // Siempre abrir `link` del create (checkout web), con o sin qrPayment; qrLink solo como respaldo.
          const order = response?.data?.order;
          const payUrl = order?.link?.trim() || order?.qrLink?.trim() || '';

          if (payUrl) {
            const webviewUrl = this.appendCardRegisteredQuery(payUrl, cardRegistered);
            console.log('✅ PayLink URL a mostrar:', webviewUrl);
            this.openPaylinkWebview(webviewUrl);
          } else {
            console.error('No se encontró link en la respuesta:', response);
            this.errorMessage = 'No se recibió un link válido en la respuesta del create.';
          }
        },
        error: (error) => {
          console.error('Error completo:', error);
          this.isLoading = false;
          this.errorMessage = error?.error?.message?.text || error?.error?.message || 'Error al crear el PayLink. Por favor, intenta nuevamente.';
          // En caso de error, el intervalo sigue corriendo (no se muestra iframe)
        }
      });
  }

  /** Overlay visible antes de asignar src al iframe (evita modal en blanco hasta un segundo clic). */
  private openPaylinkWebview(url: string): void {
    this.stopOrderIdInterval();
    this.paylinkUrl = '';
    this.showIframe = true;
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.paylinkUrl = url;
      this.cdr.detectChanges();
    });
  }

  onIframeLoad(): void {
    console.log('🎬 =================================================');
    console.log('🎬 IFRAME CARGADO EXITOSAMENTE');
    console.log('🎬 =================================================');
    console.log('🌐 URL del iframe:', this.paylinkUrl);
    console.log('📡 Listo para recibir postMessages del iframe');
    console.log('🎬 =================================================');
  }

  handleCloseButtonClick(event: MouseEvent): void {
    event.stopPropagation();
    this.closeIframe();
  }

  closeIframe(): void {
    console.log('❌ Cerrando iframe...');
    if (this.hasReceivedSdkMessage) {
      console.log('✅ Se recibió un mensaje del SDK, cerrando iframe sin abortar la orden.');
      this.handleAbortCleanup();
      return;
    }
    if (!this.orderId || !this.merchantCode || !this.orderIdentifier) {
      console.warn('No se encontraron datos suficientes para abortar, cerrando iframe localmente.');
      this.handleAbortCleanup();
      return;
    }
    this.abortOrder(this.orderId, this.merchantCode, this.orderIdentifier);
  }

  abortOrder(orderId: string, merchantCode: string, orderIdentifier: string): void {
    const environment = this.myForm.get('environment')?.value || 'QA';
    this.paylinkService.abortOrder(orderId, merchantCode, orderIdentifier, environment).subscribe({
      next: (response) => {
        console.log('Response completa:', response);
        if (response.status === 200) {
          this.handleAbortCleanup();
          return;
        }
        console.warn(`Abort order devolvió un status inesperado: ${response.status}`);
        this.handleAbortCleanup();
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 409) {
          const messageText = error?.error?.message?.text || 'La autenticación está en curso, no puede abortar el proceso en este momento';
          this.uiFeedback.toast(messageText, 6000);
          return;
        }
        console.error('Error completo:', error);
        this.handleAbortCleanup();
      }
    });
  }

  private handleAbortCleanup(): void {
    this.showIframe = false;
    this.paylinkUrl = '';
    this.hasReceivedSdkMessage = false;
    console.log('🔄 Reiniciando intervalo de OrderId...');
    this.startOrderIdInterval();
  }

  private appendCardRegisteredQuery(link: string, cardRegistered: boolean): string {
    if (!cardRegistered) {
      return link;
    }
    try {
      const url = new URL(link);
      url.searchParams.set('cardRegistered', 'true');
      return url.toString();
    } catch {
      const separator = link.includes('?') ? '&' : '?';
      return `${link}${separator}cardRegistered=true`;
    }
  }
}
